import { z } from 'zod';

import { MadBuyFrequencyCappingTimeUnit, madBuyLineItemWeekDays } from './lineItemsCoreModel';

const madBuyCampaignSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  status: nullableOptional(z.string()),
  createdOn: nullableOptional(z.string()),
  brandId: nullableOptional(z.string()),
  brand: nullableOptional(
    z.looseObject({
      id: nullableOptional(z.string()),
      name: nullableOptional(z.string()),
    }),
  ),
});

const madBuyCampaignsPageSchema = z.looseObject({
  content: z.array(madBuyCampaignSchema),
  totalElements: z.number(),
  totalPages: z.number().optional(),
  number: z.number().optional(),
  size: z.number().optional(),
});

const madBuyLineItemSchema = z.looseObject({
  id: z.string(),
  altId: nullableOptional(z.string()),
  name: z.string(),
  status: nullableOptional(z.string()),
  systemStatus: nullableOptional(z.string()),
  deliveryStatus: nullableOptional(z.string()),
  adType: nullableOptional(z.string()),
  startDate: nullableOptional(z.string()),
  endDate: nullableOptional(z.string()),
  scheduling: nullableOptional(z.boolean()),
  pacing: nullableOptional(z.string()),
  pacingPerHour: nullableOptional(z.string()),
  dayTimeActivities: nullableOptional(
    z.array(
      z.looseObject({
        day: z.enum(madBuyLineItemWeekDays),
        restrictedHours: z.boolean(),
        startHour: z.number(),
        endHour: z.number(),
      }),
    ),
  ),
  cpm: nullableOptional(z.number()),
  domain: nullableOptional(z.string()),
  margin: nullableOptional(z.number()),
  deliveredImpressions: nullableOptional(z.number()),
  impressionsGoal: nullableOptional(z.number()),
  deliveredImpressionsPct: nullableOptional(z.number()),
  upstreamCpmCapEnabled: nullableOptional(z.boolean()),
  upstreamCpmCapMinCpm: nullableOptional(z.number()),
  upstreamCpmCapMaxCpm: nullableOptional(z.number()),
  impressionTrackers: nullableOptional(z.array(z.string())),
  targeting: nullableOptional(
    z.looseObject({
      deviceTypes: z
        .array(
          z.looseObject({
            deviceTypeId: z.string(),
            excluded: z.boolean().optional().default(false),
          }),
        )
        .optional()
        .default([]),
      geoLocations: z
        .array(z.looseObject({ excluded: z.boolean().optional().default(false), type: z.string() }))
        .optional()
        .default([]),
      zipCodes: z
        .array(
          z.looseObject({
            zipCode: z.string(),
            excluded: z.boolean().optional().default(false),
          }),
        )
        .optional()
        .default([]),
      zipCodesCount: nullableOptional(z.number()),
      ipAddressesCount: nullableOptional(z.number()),
      supplyType: z.string().catch('APPS'),
      includedApps: z.array(z.unknown()).optional().default([]),
      excludedApps: z.array(z.unknown()).optional().default([]),
      includedDomains: z.array(z.unknown()).optional().default([]),
      excludedDomains: z.array(z.unknown()).optional().default([]),
      includedTailoredSupplies: z.array(z.unknown()).optional().default([]),
      tailoredSupplyExcludedApps: z.array(z.unknown()).optional().default([]),
      tailoredSupplyExcludedDomains: z.array(z.unknown()).optional().default([]),
      iabCategories: z
        .array(
          z.looseObject({
            iabCategory: z.string(),
            excluded: z.boolean().optional().default(false),
          }),
        )
        .optional()
        .default([]),
      includedChannels: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(false) }))
        .optional()
        .default([]),
      excludedChannels: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(true) }))
        .optional()
        .default([]),
      includedChannelGroups: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(false) }))
        .optional()
        .default([]),
      excludedChannelGroups: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(true) }))
        .optional()
        .default([]),
      includedContentTitles: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(false) }))
        .optional()
        .default([]),
      excludedContentTitles: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(true) }))
        .optional()
        .default([]),
      includedContentKeywords: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(false) }))
        .optional()
        .default([]),
      excludedContentKeywords: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(true) }))
        .optional()
        .default([]),
      includedContentGenres: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(false) }))
        .optional()
        .default([]),
      excludedContentGenres: z
        .array(z.looseObject({ name: z.string(), excluded: z.boolean().optional().default(true) }))
        .optional()
        .default([]),
      contentChannelsIncluded: nullableOptional(z.boolean()),
      contentChannels: z.array(z.string()).optional(),
      contentChannelGroupsIncluded: nullableOptional(z.boolean()),
      contentChannelGroups: z.array(z.string()).optional(),
      contentTitlesIncluded: nullableOptional(z.boolean()),
      contentTitles: z.array(z.string()).optional(),
      contentKeywordsIncluded: nullableOptional(z.boolean()),
      contentKeywords: z.array(z.string()).optional(),
      contentGenresIncluded: nullableOptional(z.boolean()),
      contentGenres: z.array(z.string()).optional(),
      viewability: nullableOptional(z.number()),
      ctr: nullableOptional(z.number()),
      supplyCompanies: z
        .array(z.looseObject({ id: z.string(), name: z.string() }))
        .optional()
        .default([]),
      userLanguages: z
        .array(
          z
            .looseObject({
              id: z.string().optional(),
              name: z.string(),
              code: z.string().optional(),
              isoCode2: z.string().optional(),
              isoCode3: z.string().optional(),
            })
            .transform(
              (language: {
                id?: string | undefined;
                name: string;
                code?: string | undefined;
                isoCode2?: string | undefined;
                isoCode3?: string | undefined;
              }) => ({
                ...language,
                id: language.id ?? language.isoCode3 ?? language.isoCode2 ?? language.name,
                code: language.code ?? language.isoCode3 ?? language.isoCode2,
              }),
            ),
        )
        .optional()
        .default([]),
    }),
  ),
  lineItemCreatives: nullableOptional(
    z.array(
      z.looseObject({
        id: z.string().optional(),
        creative: z.looseObject({ id: z.string(), name: z.string() }),
        weight: z.number(),
        startDate: nullableOptional(z.string()),
        endDate: nullableOptional(z.string()),
      }),
    ),
  ),
  creatives: nullableOptional(
    z.array(
      z.looseObject({
        id: z.string().optional(),
        creative: z.looseObject({ id: z.string(), name: z.string() }),
        weight: z.number(),
        startDate: nullableOptional(z.string()),
        endDate: nullableOptional(z.string()),
      }),
    ),
  ),
  conversionPixelId: nullableOptional(z.string()),
  conversionPixel: nullableOptional(
    z.looseObject({
      id: z.string(),
      name: z.string(),
      esid: z.string(),
      triggerType: nullableOptional(z.string()),
      type: nullableOptional(z.string()),
      attributionWindowType: nullableOptional(z.string()),
      attributionWindowValue: nullableOptional(z.number()),
    }),
  ),
  frequencyCapping: nullableOptional(
    z.looseObject({
      fcActive: z.boolean(),
      fcDuration: z.number(),
      fcLimit: z.number(),
      fcTimeUnit: z.enum(MadBuyFrequencyCappingTimeUnit),
    }),
  ),
  campaign: nullableOptional(
    z.looseObject({
      id: nullableOptional(z.string()),
      brand: nullableOptional(
        z.looseObject({ id: nullableOptional(z.string()), name: nullableOptional(z.string()) }),
      ),
    }),
  ),
});

const namedEntitySchema = z.looseObject({
  id: z.string(),
  name: z.string(),
});

const madBuyCreativeSchema = namedEntitySchema.extend({
  type: z.string().optional(),
  altId: z.string().optional(),
});

const madBuyConversionPixelSchema = namedEntitySchema.extend({
  altId: z.string().optional(),
  esid: z.string(),
  triggerType: nullableOptional(z.string()),
  type: nullableOptional(z.string()),
  countType: nullableOptional(z.string()),
  attributionWindowType: nullableOptional(z.string()),
  attributionWindowValue: nullableOptional(z.number()),
});

const pagedCreativesSchema = z.looseObject({
  content: z.array(madBuyCreativeSchema),
});

const pagedConversionPixelsSchema = z.looseObject({
  content: z.array(madBuyConversionPixelSchema),
});

const madBuyLineItemsPageSchema = z.looseObject({
  content: z.array(madBuyLineItemSchema),
  totalElements: z.number(),
  totalPages: z.number().optional(),
  number: z.number().optional(),
  size: z.number().optional(),
});

export {
  madBuyCampaignSchema,
  madBuyCampaignsPageSchema,
  madBuyConversionPixelSchema,
  madBuyCreativeSchema,
  madBuyLineItemSchema,
  madBuyLineItemsPageSchema,
  namedEntitySchema,
  pagedConversionPixelsSchema,
  pagedCreativesSchema,
  nullableOptional,
};

function nullableOptional<TSchema extends z.ZodType>(schema: TSchema) {
  return z.preprocess((value: unknown) => (value === null ? undefined : value), schema.optional());
}
